import { AfterContentInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MediaService } from '../../lib/media.service';
import { AppConfig } from '../../app/app.config';
import { EcgConfiguration } from '../../app/resolvers/ecg-configuration';
import { CultureMappingService } from '../utils/culture.service';
import { PostMessage, PostMessageService, PostMessageType } from '../utils/post-message.service';
import { CustomCommandState } from '../view-models/ecgstate';
import { KeyValuePair } from '../utils/key-value-pair';
import { ArrangementType, CaliperMode, Format } from '../models/enums';
import { Store } from '@ngrx/store';
import { ecgViewerActions } from '../../store/actions';
import {
    selectorCaliperMode,
    selectorCulture,
    selectorCustomCommand,
    selectorDetailsVisible,
    selectorFiducialPointsVisible,
    selectorFormat,
    selectorFullState,
    selectorIsFitBaseline,
    selectorPaperOpacity,
    selectorPaperRate,
    selectorTimeAlignment,
    selectorTraceSpacing,
    selectorZoomFactor,
} from '../../store/selectors/ecg-viegew.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { ECGState } from '../../store/reducers/ecg-viewer.reducer';
import { SeComboBoxComponent } from '../se-combobox/se-combobox.component';
import { SeStateButtonComponent } from '../se-statebutton/se-statebutton.component';
import { FormsModule } from '@angular/forms';
import { SeSliderButtonComponent } from '../se-sliderbutton/se-sliderbutton.component';
import { SeToggleButtonComponent } from '../se-togglebutton/se-togglebutton.component';
import { NgIf } from '@angular/common';
import { SeImageButtonComponent } from '../se-imagebutton/se-imagebutton.component';

@Component({
    selector: 'ecg-toolbar',
    templateUrl: './ecg-toolbar.component.html',
    styleUrls: ['./ecg-toolbar.component.css'],
    viewProviders: [PostMessageService],
    standalone: true,
    imports: [
        SeComboBoxComponent,
        SeStateButtonComponent,
        FormsModule,
        TranslatePipe,
        SeSliderButtonComponent,
        SeToggleButtonComponent,
        NgIf,
        SeImageButtonComponent,
    ],
})
export class EcgToolbarComponent implements AfterContentInit, OnDestroy {
    // Data Members
    @Input()
    ecgConfiguration!: EcgConfiguration;
    @Output()
    ecgConfigurationChange = new EventEmitter<EcgConfiguration>();
    formats: Array<number> = [];
    @Input()
    enableStudiesSpacing = false;
    caliperModes: Array<number> = [];
    arrangements: Array<KeyValuePair<string, number>> = [];
    timeAlignments: Array<number> = [];
    paperRates: Array<number> = [];
    subscriptions: Subscription[] = [];

    constructor(
        public mediaService: MediaService,
        private postMessageService: PostMessageService,
        private cultureMappingService: CultureMappingService,
        private translate: TranslateService,
        private appConfig: AppConfig,
        private ecgStore: Store<ECGState>,
        private actions: Actions,
    ) {
        this.subscriptions.push(
            this.actions.pipe(ofType(ecgViewerActions.formatMessage)).subscribe((action) => {
                this.selectedFormat = action.format;
            }),
        );
    }

    get culture() {
        return this.ecgStore.selectSignal(selectorCulture)();
    }

    @Input()
    set culture(culture: number) {
        this.ecgStore.dispatch(ecgViewerActions.cultureMessage({ culture: culture }));
    }

    get paperOpacity() {
        return this.ecgStore.selectSignal(selectorPaperOpacity)();
    }

    @Input()
    set paperOpacity(paperOpacity: number) {
        this.ecgStore.dispatch(ecgViewerActions.paperOpacityMessage({ paperOpacity: paperOpacity }));
    }

    get traceSpacing() {
        return this.ecgStore.selectSignal(selectorTraceSpacing)();
    }

    @Input()
    set traceSpacing(traceSpacing: number) {
        this.ecgStore.dispatch(ecgViewerActions.traceSpacingMessage({ traceSpacing: traceSpacing }));
    }

    get isFitBaseline() {
        return this.ecgStore.selectSignal(selectorIsFitBaseline)();
    }

    @Input()
    set isFitBaseline(isFitBaseline: boolean) {
        this.ecgStore.dispatch(ecgViewerActions.baselineMessage({ isFitBaseline: isFitBaseline }));
    }

    get studySpacing() {
        return this.ecgStore.selectSignal(selectorZoomFactor)();
    }

    @Input()
    set studySpacing(studySpacing: number) {
        this.ecgStore.dispatch(ecgViewerActions.studySpacingMessage({ studySpacing: studySpacing }));
    }

    get zoomFactor() {
        return this.ecgStore.selectSignal(selectorZoomFactor)();
    }

    @Input()
    set zoomFactor(zoomFactor: number) {
        this.ecgStore.dispatch(ecgViewerActions.zoomFactorMessage({ zoomFactor: zoomFactor }));
    }

    get selectedFormat() {
        const format = this.ecgStore.selectSignal(selectorFormat)();
        return format;
    }

    @Input()
    set selectedFormat(selectedFormat: Format) {
        this.ecgStore.dispatch(ecgViewerActions.formatMessage({ format: selectedFormat }));
    }

    get summaryVisible() {
        return this.ecgStore.selectSignal(selectorDetailsVisible)();
    }

    @Input()
    set summaryVisible(summaryVisible: boolean) {
        this.ecgStore.dispatch(ecgViewerActions.summaryVisibleMessage({ summaryVisible: summaryVisible }));
    }

    get fiducialPointsVisible() {
        return this.ecgStore.selectSignal(selectorFiducialPointsVisible)();
    }

    @Input()
    set fiducialPointsVisible(fiducialPointsVisible: boolean) {
        this.ecgStore.dispatch(
            ecgViewerActions.fiducialPointsVisibleMessage({ fiducialPointsVisible: fiducialPointsVisible }),
        );
    }

    get caliperMode() {
        return this.ecgStore.selectSignal(selectorCaliperMode)();
    }

    @Input()
    set caliperMode(caliperMode: CaliperMode) {
        this.ecgStore.dispatch(ecgViewerActions.caliperModeMessage({ caliperMode: caliperMode }));
    }

    private _selectedArrangement: KeyValuePair<string, number> | null = null;

    get selectedArrangement() {
        return this._selectedArrangement;
    }

    @Input()
    set selectedArrangement(selectedArrangement: KeyValuePair<string, number> | null) {
        if (!selectedArrangement) return;
        this._selectedArrangement = selectedArrangement;
        this.ecgStore.dispatch(ecgViewerActions.arrangementMessage({ arrangement: selectedArrangement.value }));
    }

    get selectedTimeAlignment() {
        return this.ecgStore.selectSignal(selectorTimeAlignment)();
    }

    @Input()
    set selectedTimeAlignment(selectedTimeAlignment) {
        this.ecgStore.dispatch(ecgViewerActions.timeAlignmentMessage({ timeAlignment: selectedTimeAlignment }));
    }

    get selectedPaperRate() {
        return this.ecgStore.selectSignal(selectorPaperRate)();
    }

    @Input()
    set selectedPaperRate(selectedPaperRate) {
        this.ecgStore.dispatch(ecgViewerActions.paperRateMessage({ paperRate: selectedPaperRate }));
    }

    get customCommandState() {
        return this.ecgStore.selectSignal(selectorCustomCommand)();
    }

    @Input()
    set customCommandState(customCommandState) {
        this.ecgStore.dispatch(ecgViewerActions.customCommandMessage({ customCommand: customCommandState }));
    }

    get detailsVisible() {
        return this.ecgStore.selectSignal(selectorDetailsVisible)();
    }

    // Constructor

    @Input()
    set detailsVisible(detailsVisible) {
        this.ecgStore.dispatch(ecgViewerActions.detailsVisibleMessage({ detailsVisible: detailsVisible }));
    }

    private static initEnums(enumType: any, translate: TranslateService) {
        let array = new Array<KeyValuePair<string, number>>();

        let length = Object.keys(enumType).length;

        let keys = Object.keys(enumType).slice(length / 2, length);
        let values = Object.keys(enumType)
            .slice(0, length / 2)
            .map((key) => parseInt(key));

        for (let i = 0; i < length / 2; i++) array.push(new KeyValuePair(translate.instant(keys[i]), values[i]));

        return array;
    }

    // public functions

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    ngAfterContentInit() {
        this.updateComboBoxes();
        this.listenForEvents();

        this.applyEcgConfiguration();
    }

    onFit() {
        this.ecgStore.dispatch(ecgViewerActions.fitMessage());
    }

    // private functions

    onCustomCommand() {
        const fullState = this.ecgStore.selectSignal(selectorFullState)();
        this.postMessageService.send(new PostMessage(fullState, PostMessageType.stateChanged));
    }

    private listenForEvents() {
        this.translate.onLangChange.subscribe(() => {
            let arrangementsIndex = !!this.selectedArrangement
                ? this.arrangements.indexOf(this.selectedArrangement)
                : -1;
            this.updateComboBoxes();
            this.selectedArrangement = this.arrangements[arrangementsIndex];
        });
    }

    private updateComboBoxes() {
        this.arrangements = EcgToolbarComponent.initEnums(ArrangementType, this.translate);
        this.selectedArrangement = this.arrangements[0];
    }

    private applyEcgConfiguration() {
        let cultureName = this.appConfig.getValue('culture');
        this.culture =
            this.ecgConfiguration != null && this.ecgConfiguration.culture.read
                ? this.ecgConfiguration.culture.value
                : this.cultureMappingService.getCultureTypeByCultureName(cultureName);

        if (this.ecgConfiguration == null) return;

        if (this.ecgConfiguration.detailsvisible.read) this.detailsVisible = this.ecgConfiguration.detailsvisible.value;

        if (this.ecgConfiguration.layoutConfiguration != null) {
            if (this.ecgConfiguration.layoutConfiguration.arrangement.read)
                this.selectedArrangement =
                    this.arrangements.find(
                        (arrangement) =>
                            arrangement.value == this.ecgConfiguration.layoutConfiguration.arrangement.value,
                    ) ?? null;
            if (this.ecgConfiguration.layoutConfiguration.paperRate.read)
                this.selectedPaperRate = this.ecgConfiguration.layoutConfiguration.paperRate.value;
            if (this.ecgConfiguration.layoutConfiguration.paperOpacity.read)
                this.paperOpacity = this.ecgConfiguration.layoutConfiguration.paperOpacity.value;
        }

        if (this.ecgConfiguration.customCommandConfiguration != null) {
            let customCommandState = new CustomCommandState();

            if (this.ecgConfiguration.customCommandConfiguration.visible.read)
                customCommandState.visible = this.ecgConfiguration.customCommandConfiguration.visible.value;
            if (this.ecgConfiguration.customCommandConfiguration.text.read)
                customCommandState.text = this.ecgConfiguration.customCommandConfiguration.text.value;
            if (this.ecgConfiguration.customCommandConfiguration.source.read)
                customCommandState.source = this.ecgConfiguration.customCommandConfiguration.source.value;

            this.customCommandState = customCommandState;
        }
    }
}
